import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useCookies } from "react-cookie";
import { LoginService } from "../../services/LoginService";
import UserService from "../../services/UserService";
import { UserContext } from "../../context/UserContext";
import { User } from "../../interfaces/user/User";

export function SSOCallback() {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const [cookies, setCookie] = useCookies(["access_token", "refresh_token"]);
  const [message, setMessage] = useState("Completing SSO authentication...");
  const [error, setError] = useState("");

  useEffect(() => {
    const finalize = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code") || "";
      const state = params.get("state") || "";
      const providerError = params.get("error") || "";

      const tx = LoginService.getStoredSSOTransaction();
      if (providerError) {
        setError(`SSO provider returned an error: ${providerError}`);
        return;
      }

      if (!tx || !code || !state || tx.state !== state) {
        setError("Invalid or expired SSO callback state.");
        return;
      }

      if (tx.mode === "link") {
        const accessToken = cookies.access_token || "";
        if (!accessToken) {
          setError("You must be logged in to link an SSO account.");
          return;
        }

        setMessage("Linking your SSO account...");
        const linked = await LoginService.completeMySSOLink(accessToken, {
          authorization_code: code,
          code_verifier: tx.verifier,
          redirect_uri: tx.redirect_uri,
        });

        LoginService.clearStoredSSOTransaction();

        if (!linked?.linked) {
          setError("Failed to link your SSO account.");
          return;
        }

        navigate("/profile?sso=linked", { replace: true });
        return;
      }

      const token = await LoginService.exchangeSSOAuthorizationCode({
        authorization_code: code,
        code_verifier: tx.verifier,
        organization_id: tx.organization_id,
        redirect_uri: tx.redirect_uri,
        email: tx.email,
      });

      LoginService.clearStoredSSOTransaction();

      if (!token?.access || !token.refresh) {
        setError("SSO login failed while exchanging tokens.");
        return;
      }

      const userDetails = await UserService.getMe(token.access);
      if (!userDetails) {
        setError("SSO login succeeded but user profile could not be loaded.");
        return;
      }

      const savedUser = localStorage.getItem("user");
      let autolog = "15";
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        autolog = parsed.autoLogout || "15";
      }

      const newUser: User = {
        ...userDetails,
        autoLogout: autolog,
        is_auto_logout_enabled: true,
        token,
        mfaEnabled: false,
      };

      localStorage.setItem("user", JSON.stringify(newUser));

      const date = new Date();
      setCookie("access_token", token.access, {
        expires: new Date(date.setHours(date.getHours() + 24)),
      });
      setCookie("refresh_token", token.refresh, {
        expires: date,
      });

      userContext?.setUser(newUser);
      navigate(newUser.is_superadmin ? "/sysmgmt" : "/", { replace: true });
    };

    finalize();
  }, [cookies.access_token, navigate, setCookie, userContext]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2,
        px: 2,
      }}
    >
      {!error && <CircularProgress />}
      <Typography variant="h6">{message}</Typography>
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
